import React, { useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslate } from "../../../../components/TranslationText";
import EahsForm from "../../../../components/EahsForm";
import FormSelect from "../../../../components/FormInputs/FormSelect";
import FormTextInput from "../../../../components/FormInputs/FormTextInput";
import { eformInput } from "../../../../common/cssclasses";
import { IBCFieldNames, cleanGuid } from "../utils/InternationalBreedingCertificateConstants";
import { TranslationKeys } from "../../../../constants/TranslationKeys";

const IBCStallionSection = ({ eligibleCertificates = [], isReadOnly = false, onCertificateChange }) => {
    const { translate } = useTranslate();
    const { control } = useFormContext();

    const selectedCertId = useWatch({ control, name: IBCFieldNames.SEMEN_COLLECTION_CERTIFICATE_ID });
    const stallionName = useWatch({ control, name: IBCFieldNames.STALLION_NAME });
    const serialNumber = useWatch({ control, name: IBCFieldNames.TRANSPORTED_STORED_SEMEN_CERT_SERIAL_NO });

    const certificateOptions = useMemo(() => {
        const list = (eligibleCertificates || [])
            .filter((item) => cleanGuid(item?.id))
            .map((item) => ({
                value: item.id,
                label: item.displayName || (item.referenceNumber
                    ? `${item.referenceNumber} - ${item.stallionNameEn || item.stallion || ''}`
                    : (item.stallionNameEn || item.stallion || item.name || item.id)),
                details: item,
            }));

        // If the form already has a certificate that is not in the list (e.g. edit mode), preserve it
        if (
            selectedCertId &&
            !list.some((opt) => String(opt.value).toLowerCase() === String(selectedCertId).toLowerCase())
        ) {
            const label = serialNumber && stallionName
                ? `${serialNumber} - ${stallionName}`
                : stallionName || selectedCertId;
            list.unshift({
                value: selectedCertId,
                label: label,
            });
        }

        return [{ label: translate(TranslationKeys.SELECT_COVERING_CERTIFICATE, 'Select Semen Collection Certificate'), value: "" }, ...list];
    }, [eligibleCertificates, selectedCertId, stallionName, serialNumber, translate]);

    return (
        <EahsForm isSectionTitle className={'mb-main'} ish3 title={translate(TranslationKeys.STALLION_DETAILS, 'Stallion Details')}>
            <div className="row">
                <FormSelect
                    name={IBCFieldNames.SEMEN_COLLECTION_CERTIFICATE_ID}
                    label={translate(TranslationKeys.SEMEN_OWNERSHIP_CERTIFICATE, 'Select Semen Collection Certificate')}
                    options={certificateOptions}
                    required
                    disabled={isReadOnly}
                    containerClass={eformInput}
                    onChangeCallback={onCertificateChange}
                />
                <div className={eformInput}>
                    <FormTextInput
                        name={IBCFieldNames.STALLION_NAME}
                        label={translate(TranslationKeys.STALLION, 'Stallion Name')}
                        disabled
                    />
                </div>
                <div className={eformInput}>
                    <FormTextInput
                        name={IBCFieldNames.STALLION_REG_NO}
                        label={translate(TranslationKeys.REG_NO, 'Reg No')}
                        disabled
                    />
                </div>
                <div className={eformInput}>
                    <FormTextInput
                        name={IBCFieldNames.STALLION_UELN}
                        label={translate(TranslationKeys.UELN, 'UELN')}
                        disabled
                    />
                </div>
                <div className={eformInput}>
                    <FormTextInput
                        name={IBCFieldNames.STALLION_BIRTH_DATE}
                        label={translate(TranslationKeys.BIRTH_DATE, 'Birth Date')}
                        disabled
                    />
                </div>
                <div className={eformInput}>
                    <FormTextInput
                        name={IBCFieldNames.STALLION_COLOR}
                        label={translate(TranslationKeys.COLOR, 'Color')}
                        disabled
                    />
                </div>
                <div className={eformInput}>
                    <FormTextInput
                        name={IBCFieldNames.STALLION_ORIGIN}
                        label={translate(TranslationKeys.ORIGIN, 'Origin')}
                        disabled
                    />
                </div>
                <div className={eformInput}>
                    <FormTextInput
                        name={IBCFieldNames.STALLION_BREEDER}
                        label={translate(TranslationKeys.BREEDER, 'Breeder')}
                        disabled
                    />
                </div>
                <div className={eformInput}>
                    <FormTextInput
                        name={IBCFieldNames.STALLION_OWNER}
                        label={translate(TranslationKeys.OWNER_NAME, 'Owner Name')}
                        disabled
                    />
                </div>
            </div>
        </EahsForm>
    );
};

export default IBCStallionSection;
